import maplibregl, { type Map as MapLibreMap } from "maplibre-gl";
import type { NorthMemory, NorthView } from "../../shared/north-toggle.ts";
import { compassStart, compassToggle, compassVisible, zoomStep, type CompassStep } from "../../shared/quest-compass.ts";
import { routeCanvasBearing } from "../../shared/route-canvas.ts";
import { snapToLine } from "../../shared/route-trace.ts";
import { embedMapFitInsets } from "../map-embed-measure.ts";
import {
  createMapStreetViewButton,
  mapControlButton,
  mapZoomGroup,
  MINUS_ICON,
  PLUS_ICON,
} from "../map-embed-controls.ts";
import { fitRotated } from "../../shared/rotated-fit.ts";
import type { LngLat, WorkArea } from "../../shared/types.ts";
import { VECTOR_STYLE_URL, vectorAttributionHtml } from "../../shared/basemap.ts";
import {
  routeDisplayFitPoints,
  type RouteFillFeature,
  type RoutePaintFeature,
} from "../../shared/quest-route-paint.ts";
import { embedStripFitPadding, EMBED_MAP_ATTRIB_FALLBACK_PX } from "../../shared/map-embed-padding.ts";
import { MapBasemap } from "../basemap.ts";
import {
  applyStyledRouteLineScaleForContainer,
  setStyledRouteData,
  styledRouteLayerIds,
} from "./styled-route-layers.ts";
import { t, type MessageKey } from "../i18n/index.ts";
import { COMPASS_NEEDLE_SVG, PIN_ICON_SVG } from "../map-view-controls.ts";
import { registerStreetViewMap } from "../street-view/register.ts";
import {
  clearQuestSpotMapPins,
  ROUTE_MAP_PIN_LAYER_IDS,
  setQuestSpotMapPins,
  setQuestSpotMapSelection,
  type QuestSpotMapPin,
  type QuestSpotPinDisplay,
} from "./quest-spot-map-layers.ts";
import { QUEST_SPOT_DOM_SINGLE_ZOOM, QuestSpotDomMarkers } from "./quest-spot-dom-markers.ts";
import {
  spotQuestMapFitLngLats,
  spotQuestMapFitView,
} from "../../shared/quest-spot-map-fit.ts";
import {
  questSpotDomMarkerLabelsNudgeOffset,
  questSpotDomMarkerPhotoCenterOffset,
} from "./spot-dom-marker-fit.ts";
import { mapHasStyleObject, whenMapHasStyle } from "../../shared/map-style-safe.ts";

const ROUTE_LAYER_IDS = styledRouteLayerIds("route-map");
/** A tap this close (screen px) to the route places a pin on it. */
const TAP_SNAP_PX = 44;
const FIT_MAX_ZOOM = 16;
const START_ZOOM = 13;
const TURN_MS = 400;
const RESET_MS = 600;
const ZOOM_MS = 250;
const SPOT_SELECT_PAN_MS = 600;

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

const COMPASS_LABEL: Record<CompassStep["kind"], MessageKey> = {
  north: "rt.map.compassNorth",
  restore: "rt.map.compassBack",
  canvas: "rt.map.compassRoute",
  none: "rt.map.compassNorth",
};

export type PinKind = "start" | "finish";

export interface RouteMapPins {
  start: LngLat | null;
  finish: LngLat | null;
  /** The pin being placed shows greyed until the tap lands. */
  placing: PinKind | null;
  /** Spot-only quests: every checkpoint as a map marker. */
  spotCheckpoints?: readonly QuestSpotMapPin[];
  spotPinDisplay?: QuestSpotPinDisplay;
  /** Highlight ring for the spot marker the reader picked on the map. */
  selectedSpotIndex?: number | null;
}

/**
 * The quest page's map block: route line, Start/Finish pins, and tap-to-place on the line.
 * `still`: a picture of the route for the cover slot (no gestures, no buttons; credit kept).
 */
export class RouteMap {
  private map: MapLibreMap;
  private line: LngLat[] | null = null;
  private routePaint: RoutePaintFeature[] | null = null;
  private routeFill: RouteFillFeature[] | null = null;
  private routeCanvas: WorkArea | null = null;
  private lastPins: RouteMapPins | null = null;
  private placing: PinKind | null = null;
  private onPlace: (kind: PinKind, point: LngLat) => void;
  private onMiss: () => void;
  private onSpotTap: ((point: LngLat) => void) | null;
  private onSpotMarkerSelect: ((checkpointIndex: number | null) => void) | null;
  private spotTraceMode: boolean;
  private fitted = false;
  /** Bearing the route's canvas reads upright at; 0 for north-up routes. */
  private canvasBearing = 0;
  private northMemory: NorthMemory | null = null;
  /** Where a compass turn is heading while it animates. */
  private turnTarget: NorthView | null = null;
  private compass: HTMLButtonElement;
  private needle: HTMLElement;
  private zoomIn: HTMLButtonElement;
  private zoomOut: HTMLButtonElement;
  private basemap: MapBasemap | null = null;
  private credit: HTMLElement;
  private toolbarEnd: HTMLElement;
  private startCenter: LngLat;
  private readonly fitExtraTop: () => HTMLElement[];
  private resizeObserver: ResizeObserver | null = null;
  private lastFitKey = "";
  private readonly still: boolean;
  private spotDomMarkers = new QuestSpotDomMarkers();
  private spotFitGen = 0;
  private spotSelectPanGen = 0;
  /** User panned/zoomed; skip automatic re-fit on later resizes. */
  private userMovedCamera = false;
  private initialFitDone = false;
  private homeBtn: HTMLButtonElement;
  /** Ignore map clicks that follow a drag/pan gesture. */
  private tapDown: maplibregl.Point | null = null;
  private static readonly TAP_MAX_PX = 8;

  constructor(
    container: HTMLElement,
    center: LngLat,
    handlers: {
      onPlace?: (kind: PinKind, point: LngLat) => void;
      onMiss?: () => void;
      /** Spot-only quest editor: tap the map to add a spot (no route line). */
      onSpotTap?: (point: LngLat) => void;
      /** Spot quest page: tap a marker to show that spot's card; null clears selection. */
      onSpotMarkerSelect?: (checkpointIndex: number | null) => void;
      spotTraceMode?: boolean;
      /** Toolbar pin control (defaults to route "start view"). */
      resetViewLabel?: MessageKey;
      still?: boolean;
      /** Elements below the toolbar row (e.g. tap hint) that extend the top reserve when framing. */
      fitExtraTop?: () => HTMLElement[];
      /** @deprecated use fitExtraTop */
      fitExtra?: () => HTMLElement[];
    } = {},
  ) {
    const still = handlers.still === true;
    this.still = still;
    this.fitExtraTop = handlers.fitExtraTop ?? handlers.fitExtra ?? (() => []);
    this.onPlace = handlers.onPlace ?? (() => undefined);
    this.onMiss = handlers.onMiss ?? (() => undefined);
    this.onSpotTap = handlers.onSpotTap ?? null;
    this.onSpotMarkerSelect = handlers.onSpotMarkerSelect ?? null;
    this.spotTraceMode = handlers.spotTraceMode === true;
    this.startCenter = center;
    this.map = new maplibregl.Map({
      container,
      style: VECTOR_STYLE_URL,
      center,
      zoom: START_ZOOM,
      attributionControl: false,
      interactive: !still,
      // The map sits inside a scrolling page: wheel zoom needs Ctrl/⌘ (or a trackpad
      // pinch), and on touch one finger scrolls the page while two fingers move the map.
      cooperativeGestures: true,
      locale: {
        "CooperativeGesturesHandler.WindowsHelpText": t("rt.map.gestureWindows"),
        "CooperativeGesturesHandler.MacHelpText": t("rt.map.gestureMac"),
        "CooperativeGesturesHandler.MobileHelpText": t("rt.map.gestureMobile"),
      },
      pitchWithRotate: false,
      dragRotate: false,
    });
    (container as HTMLElement & { __map?: MapLibreMap; __routeMap?: RouteMap }).__map = this.map;
    (container as HTMLElement & { __routeMap?: RouteMap }).__routeMap = this;
    this.map.on("mousedown", (e) => {
      if (e.originalEvent) this.tapDown = e.point;
    });
    this.map.on("touchstart", (e) => {
      if (e.originalEvent) this.tapDown = e.point;
    });
    this.map.on("click", (e) => this.tap(e.point, [e.lngLat.lng, e.lngLat.lat]));
    this.credit = document.createElement("div");
    this.credit.className = "route-map-credit";
    this.credit.innerHTML = vectorAttributionHtml();
    container.appendChild(this.credit);

    this.compass = document.createElement("button");
    this.compass.type = "button";
    this.compass.className = "route-map-compass";
    this.needle = document.createElement("span");
    this.needle.className = "route-map-compass-needle";
    this.needle.setAttribute("aria-hidden", "true");
    this.needle.innerHTML = COMPASS_NEEDLE_SVG;
    this.compass.appendChild(this.needle);
    this.compass.addEventListener("click", (e) => {
      e.stopPropagation();
      this.toggleCompass();
    });

    const resetLabel = handlers.resetViewLabel ?? "rt.map.reset";
    this.homeBtn = mapControlButton("route-map-home", t(resetLabel), PIN_ICON_SVG, () => this.resetView());
    this.paintHomeLabel(resetLabel);
    const streetView = createMapStreetViewButton();
    registerStreetViewMap({
      map: this.map,
      button: streetView,
      getCenter: () => {
        const c = this.map.getCenter();
        return { lng: c.lng, lat: c.lat };
      },
    });
    this.zoomOut = mapControlButton("route-map-zoom-btn", t("rt.map.zoomOut"), MINUS_ICON, () => this.zoomBy(-1));
    this.zoomIn = mapControlButton("route-map-zoom-btn", t("rt.map.zoomIn"), PLUS_ICON, () => this.zoomBy(1));
    const zoom = mapZoomGroup(this.zoomOut, this.zoomIn);
    const primary = document.createElement("div");
    primary.className = "route-map-toolbar-primary";
    primary.append(this.compass, this.homeBtn, streetView, zoom);
    this.toolbarEnd = document.createElement("div");
    this.toolbarEnd.className = "route-map-toolbar-end";
    const toolbar = document.createElement("div");
    toolbar.className = "route-map-toolbar";
    toolbar.append(primary, this.toolbarEnd);
    if (!still) {
      container.appendChild(toolbar);
      this.basemap = new MapBasemap({
        map: this.map,
        creditEl: this.credit,
        onStyleReady: () => this.repaintRoute(),
      });
      primary.insertBefore(this.basemap.getToggleElement(), this.compass);
      void this.basemap.boot();
    }

    this.map.on("rotate", () => this.paintCompass());
    this.map.on("rotatestart", (e: { originalEvent?: unknown }) => {
      if (e.originalEvent) {
        this.northMemory = null;
        this.userMovedCamera = true;
      }
    });
    this.map.on("dragstart", (e: { originalEvent?: unknown }) => {
      if (e.originalEvent) this.userMovedCamera = true;
    });
    this.map.on("zoomstart", (e: { originalEvent?: unknown }) => {
      if (e.originalEvent) this.userMovedCamera = true;
    });
    this.map.on("moveend", () => {
      this.turnTarget = null;
      this.paintCompass();
    });
    this.map.on("zoom", () => this.paintZoom());
    this.paintCompass();
    this.paintZoom();
    this.resizeObserver = new ResizeObserver(() => this.onMapResize());
    this.resizeObserver.observe(container);
    if (container.parentElement) this.resizeObserver.observe(container.parentElement);
  }

  private onMapResize(): void {
    this.map.resize();
    const box = this.map.getContainer();
    if (this.lastPins) this.paintPins(this.lastPins);
    const sel = this.lastPins?.selectedSpotIndex;
    if (sel != null) this.panToSelectedSpot(sel);
    else if (!this.userMovedCamera) {
      this.scheduleSpotFit(undefined, true);
    }
    applyStyledRouteLineScaleForContainer(this.map, ROUTE_LAYER_IDS, box.clientWidth, box.clientHeight);
  }

  /** Re-frame when overlay controls (e.g. Edit ↗, tap hint) change size without a map resize. */
  refitForOverlays(): void {
    if (!this.userMovedCamera) this.scheduleSpotFit(undefined, !this.initialFitDone);
  }

  /** Same framing as the pin (fit-all) control. */
  private fitAllView(bearing?: number, duration = 0): void {
    const start = compassStart(bearing ?? this.canvasBearing);
    this.northMemory = start.memory;
    if (this.fitPoints().length >= 1) this.fit(start.target.bearing, duration);
    else {
      this.map.easeTo({
        center: this.startCenter,
        zoom: START_ZOOM,
        bearing: start.target.bearing,
        pitch: 0,
        duration,
      });
    }
    this.initialFitDone = true;
  }

  setResetViewLabel(key: MessageKey): void {
    this.paintHomeLabel(key);
  }

  private paintHomeLabel(key: MessageKey): void {
    const label = t(key);
    this.homeBtn.title = label;
    this.homeBtn.setAttribute("aria-label", label);
  }

  /** Spot thumbnail maps frame every located spot (editor preview + spot quest page). */
  private spotDomFramingActive(): boolean {
    return (
      this.effectiveSpotPinDisplay(this.lastPins?.spotPinDisplay ?? "routeOrdinal") === "spotThumbnail" &&
      spotQuestMapFitLngLats(this.lastPins?.spotCheckpoints).length > 0
    );
  }

  /**
   * Spot / route framing after the map style is ready, the container has a real size,
   * and checkpoint coordinates are on the map (avoids locking START_ZOOM on a 0×0 layout).
   */
  private scheduleSpotFit(bearing?: number, force = false, duration = 0): void {
    if (this.fitPoints().length < 1) return;
    if (!force && this.lastPins?.selectedSpotIndex != null) return;
    if (force) {
      this.lastFitKey = "";
      this.fitted = false;
    }
    const gen = ++this.spotFitGen;
    const b = bearing ?? this.turnTarget?.bearing ?? this.map.getBearing();
    const spotThumbMode = this.spotDomFramingActive();
    const attempt = (tries: number): void => {
      if (gen !== this.spotFitGen) return;
      this.map.resize();
      const box = this.map.getContainer();
      const ready = box.clientWidth > 0 && box.clientHeight > 0 && mapHasStyleObject(this.map);
      if (!ready) {
        if (tries < 120) requestAnimationFrame(() => attempt(tries + 1));
        return;
      }
      if (spotThumbMode) {
        const markerCount = spotQuestMapFitLngLats(this.lastPins?.spotCheckpoints).length;
        const domCount = this.map.getContainer().querySelectorAll(".quest-spot-dom-marker").length;
        if (domCount < markerCount && tries < 120) {
          requestAnimationFrame(() => attempt(tries + 1));
          return;
        }
      }
      this.fitAllView(b, duration);
    };
    const run = (): void => {
      requestAnimationFrame(() => attempt(0));
    };
    whenMapHasStyle(this.map, () => {
      if (this.spotDomFramingActive()) requestAnimationFrame(run);
      else run();
    });
  }

  /** Right side of the top toolbar (e.g. 편집 ↗). */
  setToolbarEnd(child: HTMLElement | null): void {
    this.toolbarEnd.replaceChildren(...(child ? [child] : []));
    this.refitForOverlays();
  }

  /** `canvas` is the editor canvas the route was drawn in; the map opens turned to its bearing. */
  setRoute(
    line: LngLat[] | null,
    canvas: WorkArea | null = null,
    paint: RoutePaintFeature[] | null = null,
    fill: RouteFillFeature[] | null = null,
  ): void {
    this.routeCanvas = canvas;
    const bearing = routeCanvasBearing(canvas);
    const changed =
      JSON.stringify(line) !== JSON.stringify(this.line) ||
      bearing !== this.canvasBearing ||
      JSON.stringify(paint) !== JSON.stringify(this.routePaint) ||
      JSON.stringify(fill) !== JSON.stringify(this.routeFill);
    this.routePaint = paint;
    this.routeFill = fill;
    this.line = line;
    if (changed) {
      this.canvasBearing = bearing;
      this.northMemory = null;
      this.lastFitKey = "";
    }
    const paintRoute = () => {
      setStyledRouteData(this.map, ROUTE_LAYER_IDS, paint, line, undefined, fill);
      const box = this.map.getContainer();
      applyStyledRouteLineScaleForContainer(this.map, ROUTE_LAYER_IDS, box.clientWidth, box.clientHeight);
      this.basemap?.reconcileLayerStack();
      const frame = () => {
        if (changed || !this.fitted) {
          if (this.fitPoints().length >= 1) this.scheduleSpotFit(this.canvasBearing, changed);
          else this.fit(this.canvasBearing);
        }
        applyStyledRouteLineScaleForContainer(this.map, ROUTE_LAYER_IDS, box.clientWidth, box.clientHeight);
      };
      const scheduleFrame = () => requestAnimationFrame(frame);
      if (!this.map.isMoving()) scheduleFrame();
      else this.map.once("moveend", scheduleFrame);
    };
    whenMapHasStyle(this.map, paintRoute);
  }

  private repaintRoute(): void {
    this.setRoute(this.line, this.routeCanvas, this.routePaint, this.routeFill);
    if (this.lastPins) this.paintPins(this.lastPins);
  }

  setPins(pins: RouteMapPins): void {
    const prev = this.lastPins;
    this.lastPins = pins;
    this.paintPins(pins, prev);
  }

  /** Drop cached fit bounds so the next refit uses moved or new spot pins. */
  invalidateFit(): void {
    this.lastFitKey = "";
    this.fitted = false;
  }

  private paintPins(pins: RouteMapPins, prev: RouteMapPins | null = this.lastPins): void {
    const prevSpots = prev?.spotCheckpoints;
    const nextSpots = pins.spotCheckpoints;
    const selectionChanged = prev?.selectedSpotIndex !== pins.selectedSpotIndex;
    const selectionOnly =
      prev != null &&
      JSON.stringify(prevSpots ?? null) === JSON.stringify(nextSpots ?? null) &&
      prev?.spotPinDisplay === pins.spotPinDisplay &&
      prev?.start === pins.start &&
      prev?.finish === pins.finish &&
      prev?.placing === pins.placing &&
      selectionChanged;
    const spotsChanged =
      JSON.stringify(prevSpots ?? null) !== JSON.stringify(nextSpots ?? null) ||
      prev?.spotPinDisplay !== pins.spotPinDisplay ||
      prev?.start !== pins.start ||
      prev?.finish !== pins.finish ||
      prev?.placing !== pins.placing;
    if (spotsChanged) this.invalidateFit();
    if (selectionChanged) {
      this.spotDomMarkers.setSelectedIndex(pins.selectedSpotIndex);
      if (pins.selectedSpotIndex != null) {
        this.spotFitGen++;
        this.scheduleSpotSelectPan(pins.selectedSpotIndex);
      }
      if (selectionOnly) return;
    }
    const merged: QuestSpotMapPin[] = [...(nextSpots ?? [])];
    const addEndpoint = (kind: PinKind, at: LngLat | null) => {
      if (!at) return;
      merged.push({
        lng: at[0],
        lat: at[1],
        kind,
        label: kind === "start" ? "S" : "F",
        placing: pins.placing === kind,
      });
    };
    addEndpoint("finish", pins.finish);
    addEndpoint("start", pins.start);
    const display = this.effectiveSpotPinDisplay(pins.spotPinDisplay ?? "routeOrdinal");
    const spotPins = merged.filter((p) => p.kind === "spot");
    if (display === "spotThumbnail" && spotPins.length) {
      this.spotDomMarkers.sync(this.map, spotPins, {
        selectedIndex: pins.selectedSpotIndex,
        onSelect: this.onSpotMarkerSelect ?? undefined,
      });
    } else {
      this.spotDomMarkers.clear();
    }
    const layerPins =
      display === "spotThumbnail" ? merged.filter((p) => p.kind !== "spot") : merged;
    if (layerPins.length) {
      setQuestSpotMapPins(this.map, ROUTE_MAP_PIN_LAYER_IDS, layerPins, {
        circleRadius: 12,
        display: display === "spotThumbnail" ? "routeOrdinal" : display,
      });
    } else {
      clearQuestSpotMapPins(this.map, ROUTE_MAP_PIN_LAYER_IDS);
    }
    if (display === "spotThumbnail") {
      setQuestSpotMapSelection(this.map, ROUTE_MAP_PIN_LAYER_IDS, null);
    } else {
      const selIdx = pins.selectedSpotIndex;
      const selectedPin =
        selIdx != null
          ? merged.find((p) => p.kind === "spot" && p.index === selIdx) ??
            merged.filter((p) => p.kind === "spot")[selIdx] ??
            null
          : null;
      setQuestSpotMapSelection(
        this.map,
        ROUTE_MAP_PIN_LAYER_IDS,
        selectedPin ? { lng: selectedPin.lng, lat: selectedPin.lat } : null,
      );
    }
    if ((spotsChanged || !this.fitted) && merged.length) {
      const force = spotsChanged;
      if (this.spotDomFramingActive()) {
        requestAnimationFrame(() => this.scheduleSpotFit(undefined, force));
      } else {
        this.scheduleSpotFit(undefined, force);
      }
    }
  }

  setPlacing(kind: PinKind | null): void {
    this.placing = kind;
    this.map.getCanvas().style.cursor = kind ? "crosshair" : "";
  }

  resize(): void {
    this.map.resize();
  }

  /** After the container was hidden or changed size: resize, then frame the route again. */
  relayout(): void {
    this.lastFitKey = "";
    this.map.resize();
    this.fit(this.turnTarget?.bearing ?? this.map.getBearing());
    const box = this.map.getContainer();
    applyStyledRouteLineScaleForContainer(this.map, ROUTE_LAYER_IDS, box.clientWidth, box.clientHeight);
  }

  destroy(): void {
    this.resizeObserver?.disconnect();
    this.spotDomMarkers.clear();
    clearQuestSpotMapPins(this.map, ROUTE_MAP_PIN_LAYER_IDS);
    this.map.remove();
  }

  /** Quest map always uses photo markers when spotThumbnail is requested (incl. small editor preview). */
  private effectiveSpotPinDisplay(display: QuestSpotPinDisplay): QuestSpotPinDisplay {
    if (display === "spotTitleLabel") return "spotThumbnail";
    return display;
  }

  private tap(screen: maplibregl.Point, at: LngLat): void {
    if (this.tapDown) {
      const moved = Math.hypot(screen.x - this.tapDown.x, screen.y - this.tapDown.y);
      this.tapDown = null;
      if (moved > RouteMap.TAP_MAX_PX) return;
    }
    const pinDisplay = this.effectiveSpotPinDisplay(this.lastPins?.spotPinDisplay ?? "routeOrdinal");
    if (!this.placing && pinDisplay === "spotThumbnail" && this.onSpotMarkerSelect) {
      this.onSpotMarkerSelect(null);
      return;
    }
    if (!this.placing && this.onSpotMarkerSelect && pinDisplay !== "spotThumbnail") {
      const layers = [ROUTE_MAP_PIN_LAYER_IDS.label];
      try {
        if (this.map.getLayer(ROUTE_MAP_PIN_LAYER_IDS.circle)) layers.push(ROUTE_MAP_PIN_LAYER_IDS.circle);
      } catch {
        /* torn down */
      }
      const features = this.map.queryRenderedFeatures(screen, { layers });
      const spotHit = features.find((f) => f.properties?.kind === "spot");
      if (spotHit) {
        const idx = Number(spotHit.properties?.index);
        if (Number.isInteger(idx) && idx >= 0) {
          this.onSpotMarkerSelect(idx);
          return;
        }
      }
    }
    if (this.spotTraceMode && this.onSpotTap && !this.placing) {
      this.onSpotTap(at);
      return;
    }
    if (!this.placing || !this.line || this.line.length < 2) return;
    const snap = snapToLine(at, this.line);
    if (!snap) return;
    const onScreen = this.map.project(snap.point);
    if (Math.hypot(onScreen.x - screen.x, onScreen.y - screen.y) > TAP_SNAP_PX) {
      this.onMiss();
      return;
    }
    this.onPlace(this.placing, snap.point);
  }

  /** Pan/zoom to show a selected checkpoint (no-op for null). */
  panToSelectedSpot(index: number | null): void {
    if (index == null) return;
    const spots = this.lastPins?.spotCheckpoints;
    const pin =
      spots?.find((p) => p.kind === "spot" && p.index === index) ??
      spots?.filter((p) => p.kind === "spot")[index] ??
      null;
    if (pin && Number.isFinite(pin.lng) && Number.isFinite(pin.lat)) {
      this.panToSpot([pin.lng, pin.lat] as LngLat);
      return;
    }
    this.centerOnSelectedSpot(index);
  }

  /** Pan/zoom to `at` for spot selection (does not refit all spots). */
  panToSpot(at: LngLat): void {
    this.spotFitGen++;
    const gen = ++this.spotSelectPanGen;
    const run = (): void => {
      if (gen !== this.spotSelectPanGen) return;
      this.map.resize();
      const zoom = Math.max(this.map.getZoom(), QUEST_SPOT_DOM_SINGLE_ZOOM);
      const bearing = this.map.getBearing();
      const pitch = this.map.getPitch();
      const offset = this.selectedSpotDomMarkerCenterOffset(at);
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (reduced) {
        this.map.jumpTo({ center: at, zoom, bearing, pitch, ...(offset ? { offset } : {}) });
        return;
      }
      this.map.easeTo({
        center: at,
        zoom,
        bearing,
        pitch,
        ...(offset ? { offset } : {}),
        duration: SPOT_SELECT_PAN_MS,
        easing: easeInOutCubic,
        essential: true,
      });
    };
    requestAnimationFrame(() => requestAnimationFrame(run));
  }

  /** Defer spot pan until selected-marker layout (card open, is-selected) has settled. */
  private scheduleSpotSelectPan(index: number): void {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (this.lastPins?.selectedSpotIndex !== index) return;
        this.centerOnSelectedSpot(index);
      });
    });
  }

  /**
   * Pixel offset for easeTo/jumpTo so the selected DOM marker's box center lands on the canvas center
   * (marker lng/lat anchor stays at `at`; photo+title layout shifts the visual center).
   */
  private selectedSpotDomMarkerCenterOffset(at: LngLat): [number, number] | null {
    if (this.lastPins?.selectedSpotIndex == null) return null;
    if (this.effectiveSpotPinDisplay(this.lastPins.spotPinDisplay ?? "routeOrdinal") !== "spotThumbnail") return null;
    const selected = this.map.getContainer().querySelector<HTMLElement>(".quest-spot-dom-marker.is-selected");
    if (!selected) return null;
    const photo = selected.querySelector<HTMLElement>(".quest-spot-dom-marker-photo");
    try {
      const anchor = this.map.project(at);
      const canvas = this.map.getCanvas();
      const cr = canvas.getBoundingClientRect();
      const mr = (photo ?? selected).getBoundingClientRect();
      const visX = mr.left + mr.width / 2 - cr.left;
      const visY = mr.top + mr.height / 2 - cr.top;
      const offX = visX - anchor.x;
      const offY = visY - anchor.y;
      if (!Number.isFinite(offX) || !Number.isFinite(offY)) return null;
      return [-offX, -offY];
    } catch {
      return null;
    }
  }

  /** Pan to a selected checkpoint; deselect (null) does not move the camera. */
  private centerOnSelectedSpot(index: number): void {
    const spots = this.lastPins?.spotCheckpoints;
    if (!spots?.length) return;
    const pin =
      spots.find((p) => p.kind === "spot" && p.index === index) ??
      spots.filter((p) => p.kind === "spot")[index] ??
      null;
    if (!pin || !Number.isFinite(pin.lng) || !Number.isFinite(pin.lat)) return;
    this.panToSpot([pin.lng, pin.lat] as LngLat);
  }

  /** Center photo marker circles in the map; nudge if titles clip at the edges. */
  private centerSpotDomMarkerGroup(iter = 0): void {
    if (this.lastPins?.selectedSpotIndex != null) return;
    const box = this.map.getContainer();
    const offset = questSpotDomMarkerPhotoCenterOffset(box);
    if (offset) {
      const [dx, dy] = offset;
      if (Math.abs(dx) >= 1 || Math.abs(dy) >= 1) {
        try {
          this.map.panBy(offset, { duration: 0 });
        } catch {
          return;
        }
        if (iter < 4) {
          requestAnimationFrame(() => this.centerSpotDomMarkerGroup(iter + 1));
          return;
        }
      }
    }
    this.nudgeSpotDomMarkerLabelsIntoView();
  }

  /** Pan just enough that title labels (and markers) stay inside the map. */
  private nudgeSpotDomMarkerLabelsIntoView(tries = 0): void {
    if (this.lastPins?.selectedSpotIndex != null) return;
    const box = this.map.getContainer();
    const nudge = questSpotDomMarkerLabelsNudgeOffset(box);
    if (!nudge) return;
    try {
      this.map.panBy(nudge, { duration: 0 });
    } catch {
      return;
    }
    if (tries < 4) requestAnimationFrame(() => this.nudgeSpotDomMarkerLabelsIntoView(tries + 1));
  }

  private fitPoints(): LngLat[] {
    const spotPts = spotQuestMapFitLngLats(this.lastPins?.spotCheckpoints);
    if ((this.spotTraceMode || this.spotDomFramingActive()) && spotPts.length) return spotPts;
    const routePts = routeDisplayFitPoints(this.line, this.routePaint, this.routeFill);
    if (routePts.length >= 2) return routePts;
    if (spotPts.length) return spotPts;
    return routePts;
  }

  /** Frame the route turned to `bearing`. */
  private fit(bearing: number, duration = 0): void {
    const points = this.fitPoints();
    if (points.length === 0) {
      if (duration > 0) this.map.easeTo({ bearing, duration });
      else this.map.setBearing(bearing);
      return;
    }
    const spotThumbMode =
      this.effectiveSpotPinDisplay(this.lastPins?.spotPinDisplay ?? "routeOrdinal") === "spotThumbnail" &&
      spotQuestMapFitLngLats(this.lastPins?.spotCheckpoints).length > 0;
    const box = this.map.getContainer();
    const baseInsets = this.still
      ? embedStripFitPadding(12, EMBED_MAP_ATTRIB_FALLBACK_PX + 28)
      : embedMapFitInsets(box, this.fitExtraTop());
    let view: { center: LngLat; zoom: number; bearing: number } | null = null;
    if (spotThumbMode) {
      view = spotQuestMapFitView(this.lastPins?.spotCheckpoints ?? [], bearing, box.clientWidth, box.clientHeight, baseInsets);
    } else if (points.length >= 2) {
      view = fitRotated(points, bearing, {
        width: box.clientWidth,
        height: box.clientHeight,
        padding: baseInsets,
        maxZoom: FIT_MAX_ZOOM,
      });
    } else {
      view = { center: points[0]!, zoom: START_ZOOM, bearing };
    }
    const fitKey = `${box.clientWidth}x${box.clientHeight}:${JSON.stringify(baseInsets)}:${spotThumbMode}:${points.length}:${JSON.stringify(points)}`;
    if (duration === 0 && fitKey === this.lastFitKey && this.fitted) {
      this.map.setBearing(bearing);
      return;
    }
    if (!view || box.clientWidth === 0 || box.clientHeight === 0) {
      this.map.setBearing(bearing);
      return;
    }
    const afterSpotFit = (): void => {
      if (spotThumbMode) this.centerSpotDomMarkerGroup();
    };
    try {
      if (duration > 0) {
        this.map.easeTo({ ...view, duration });
        if (spotThumbMode) this.map.once("moveend", afterSpotFit);
      } else {
        this.map.jumpTo(view);
        if (spotThumbMode) {
          this.map.once("moveend", afterSpotFit);
          if (!this.map.isMoving()) requestAnimationFrame(afterSpotFit);
        }
      }
      this.fitted = true;
      this.lastFitKey = fitKey;
    } catch {
      try {
        this.map.setBearing(bearing);
      } catch {
        /* Home browse may update the main map while the inline map is still booting. */
      }
    }
  }

  private currentView(): NorthView {
    return this.turnTarget ?? { bearing: this.map.getBearing(), pitch: this.map.getPitch() };
  }

  private toggleCompass(): void {
    const step = compassToggle(this.currentView(), this.northMemory, this.canvasBearing);
    this.northMemory = step.memory;
    if (step.kind === "none") return;
    this.fit(step.target.bearing, TURN_MS);
    this.turnTarget = this.map.isEasing() ? step.target : null;
    this.paintCompass();
  }

  /** Back to the view the map opened at: route canvas bearing, or all spots on spot quests. */
  private resetView(): void {
    this.spotSelectPanGen++;
    this.spotFitGen++;
    this.invalidateFit();
    this.userMovedCamera = false;
    this.fitAllView(undefined, RESET_MS);
    this.turnTarget = this.map.isEasing() ? compassStart(this.canvasBearing).target : null;
    this.paintCompass();
  }

  private zoomBy(dir: 1 | -1): void {
    const zoom = zoomStep(this.map.getZoom(), dir, this.map.getMinZoom(), this.map.getMaxZoom());
    this.map.easeTo({ zoom, duration: ZOOM_MS });
  }

  private paintZoom(): void {
    const zoom = this.map.getZoom();
    this.zoomIn.disabled = zoom >= this.map.getMaxZoom();
    this.zoomOut.disabled = zoom <= this.map.getMinZoom();
  }

  private paintCompass(): void {
    const bearing = this.map.getBearing();
    this.needle.style.transform = `rotate(${-bearing}deg)`;
    const view = this.currentView();
    this.compass.hidden = !compassVisible(view.bearing, this.canvasBearing) && !compassVisible(bearing, this.canvasBearing);
    const label = t(COMPASS_LABEL[compassToggle(view, this.northMemory, this.canvasBearing).kind]);
    this.compass.title = label;
    this.compass.setAttribute("aria-label", label);
  }
}
